import './src/styles/global.css';
import { ResolveLanguage } from './src/localization/i18n';
import React, { useEffect } from 'react';
import { SnackbarProvider } from 'notistack';
import NotificationProvider from './src/components/root/notification';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { I18nextProvider, useTranslation } from 'react-i18next';
import { isWeb } from './src/utilities/platform';
import { StyledNotiStackContent, THEME_GREEN } from './src/constants/style';
import { green, blue, red, yellow, lightGreen, deepOrange, orange } from '@mui/material/colors';
import { CheckCircle, Error, Warning, Info, AutoAwesome, DoDisturbAlt, Lightbulb, LightbulbCircle, CircleNotifications, NotificationsActive } from '@mui/icons-material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const theme = createTheme({
  palette: {
    primary: {
      main: THEME_GREEN,
    },
  },
});

const LocalizationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  return (<I18nextProvider i18n={i18n}>{children}</I18nextProvider>);
};

// Applies the per-route language policy (see localization/i18n.ts / LANGUAGE_ENABLED_ROUTES).
//
// This has to live in wrapPageElement rather than wrapRootElement: the root element is mounted once
// and never re-rendered, but Gatsby is an SPA, so navigating from Clinic Check In to the homepage
// never re-runs i18n's module-level init. Without re-resolving per route, a patient who chose
// Chinese on Check In would carry it onto every page they clicked through to afterwards.
//
// Running in an effect (not during render) also means the language only changes after hydration,
// keeping the first client render identical to the server-rendered HTML.
const RouteLanguageSync = ({ location, children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = ResolveLanguage(location.pathname, location.search);
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [location.pathname, location.search]);

  return children;
};

export const wrapRootElement = ({ element, props }) => {
  return (
    <SnackbarProvider iconVariant={{
      success: <CheckCircle color={lightGreen[500]} sx={{ mr: 1 }} />,
      error: <Error color={red[500]} sx={{ mr: 1 }} />,
      warning: <NotificationsActive color={orange[500]} sx={{ mr: 1 }} />,
      info: <AutoAwesome color={blue[500]} sx={{ mr: 1 }} />,
    }} Components={{
      success: StyledNotiStackContent,
      error: StyledNotiStackContent,
      warning: StyledNotiStackContent,
      info: StyledNotiStackContent,
    }}>
      <NotificationProvider>
        <ThemeProvider theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <LocalizationProvider>{element}</LocalizationProvider>
          </DndProvider>
        </ThemeProvider>
      </NotificationProvider>
    </SnackbarProvider>
  );
};

// Unlike wrapRootElement, this re-renders on every navigation and receives the current location.
export const wrapPageElement = ({ element, props }) => {
  return (<RouteLanguageSync location={props.location}>{element}</RouteLanguageSync>);
};